import axios from 'Src/helpers/api/axiosInstance';
import { getSessions } from 'Src/helpers/session/session';

export interface HelpKnowledgeSection {
  id: string;
  title: string;
  summary?: string;
  items?: string[];
  anchorId?: string;
  actionId?: string;
}

export interface HelpKnowledgeFaq {
  question: string;
  answer: string;
  keywords?: string[];
}

export interface HelpKnowledgeArticle {
  _id: string;
  key: string;
  locale: string;
  title: string;
  summary?: string;
  sections?: HelpKnowledgeSection[];
  faq?: HelpKnowledgeFaq[];
  screenIds?: string[];
  roles?: string[];
  categories?: string[];
  keywords?: string[];
  anchorIds?: string[];
  actionIds?: string[];
  relatedArticleKeys?: string[];
  order?: number;
  version?: number;
  publishedAt?: string;
  updatedAt?: string;
}

export interface HelpContextResponse {
  screenId: string;
  locale: string;
  revision: string;
  articles: HelpKnowledgeArticle[];
}

export interface HelpSearchResponse {
  query: string;
  scope: 'screen' | 'all';
  screenId?: string;
  locale: string;
  total: number;
  articles: HelpKnowledgeArticle[];
}

interface CacheEntry<T> {
  expiresAt: number;
  value: T;
}

const CONTEXT_TTL_MS = 5 * 60 * 1000;
const SEARCH_TTL_MS = 60 * 1000;
const contextCache = new Map<string, CacheEntry<HelpContextResponse>>();
const contextRequests = new Map<string, Promise<HelpContextResponse>>();
const searchCache = new Map<string, CacheEntry<HelpSearchResponse>>();

function getAuthConfig(params?: any): any {
  const { roleSession } = getSessions();
  if (!roleSession || !roleSession.key) {
    return undefined;
  }
  return {
    headers: { usid: roleSession.key },
    params
  };
}

function getCached<T>(cache: Map<string, CacheEntry<T>>, key: string): T | undefined {
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (entry.expiresAt <= Date.now()) {
    cache.delete(key);
    return undefined;
  }
  return entry.value;
}

export function getHelpContext(screenId: string, locale = 'en-GB'): Promise<HelpContextResponse> {
  const key = `${screenId}|${locale}`;
  const cached = getCached(contextCache, key);
  if (cached) return Promise.resolve(cached);
  const inFlight = contextRequests.get(key);
  if (inFlight) return inFlight;

  const config = getAuthConfig({ locale });
  if (!config) return Promise.reject(new Error('A role session is required to load help content.'));

  const request = axios
    .get(`${window.apiBase}/i/help/context/${encodeURIComponent(screenId)}`, config)
    .then(response => {
      const value = response.data as HelpContextResponse;
      contextCache.set(key, { value, expiresAt: Date.now() + CONTEXT_TTL_MS });
      return value;
    })
    .finally(() => contextRequests.delete(key));
  contextRequests.set(key, request);
  return request;
}

export function searchHelpKnowledge(options: {
  query: string;
  screenId: string;
  scope: 'screen' | 'all';
  locale?: string;
}): Promise<HelpSearchResponse> {
  const locale = options.locale || 'en-GB';
  const query = options.query
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 120);
  const key = `${options.scope}|${options.screenId}|${locale}|${query.toLocaleLowerCase()}`;
  const cached = getCached(searchCache, key);
  if (cached) return Promise.resolve(cached);
  const config = getAuthConfig({
    q: query,
    screenId: options.screenId,
    scope: options.scope,
    locale
  });
  if (!config) return Promise.reject(new Error('A role session is required to search help content.'));

  return axios.get(`${window.apiBase}/i/help/search`, config).then(response => {
    const value = response.data as HelpSearchResponse;
    searchCache.set(key, { value, expiresAt: Date.now() + SEARCH_TTL_MS });
    return value;
  });
}
